/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FileType, isTransport, isWasting, IWorkflow, LANDSCAPE_ASPECT_RATIO } from '@midas/shared/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { VideoThumbnailComponent } from './video-thumbnail.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const videojs = (window as any).videojs;

@Component({
  selector: 'midas-vjs-player',
  template: `
    <video
      #target
      class="video-js"
      [style.display]="!videoUploadComplete || videoCheckSumFail ? 'none' : 'block'"
      crossorigin="anonymous"
      controls
      muted
      playsinline
      [preload]="preload"
      (error)="handleVideoError($event)"
    ></video>
    @if (videoType === FileType.WastingVideo) {
      <midas-video-thumbnail
        [workflow]="workflow"
        [videoCheckSumFail]="videoCheckSumFail"
        [width]="width"
        [height]="height"
        [videoNotAvailable]="videoNotAvailable"
      />
    }
    @if (showVideoError) {
      <div>{{ videoLoadErrorMessage }}</div>
      <div>{{ videoLoadErrorDetail }}</div>
    }
  `,
  styles: [
    `
      .video-js .vjs-time-control {
        display: block;
      }
      .video-js .vjs-remaining-time {
        display: block;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, TranslocoModule, VideoThumbnailComponent],
})
export class VjsPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('target', { static: true }) target!: ElementRef<HTMLVideoElement>;

  @Input() preload: 'auto' | 'metadata' = 'metadata';

  @Input() @HostBinding('style.width.px') width = 220;
  @Input() @HostBinding('style.height.px') height = 140;

  @Output() videoFailedEvent = new EventEmitter();
  @Output() metadataLoaded = new EventEmitter<{ duration: number }>();

  // See options: https://videojs.com/guides/options

  @Input() fluid = false;
  @Input() aspectRatio = LANDSCAPE_ASPECT_RATIO;
  @Input() autoplay = false;
  @Input() disabled = false;
  @Input() type = 'video/mp4';
  @Input() workflow?: IWorkflow;
  @Input() videoType: FileType = FileType.WastingVideo;
  @Input() inactivityTimeout = 0; // No inactivity timeout treats the user as always active. Controls will not hide.
  @Input() videoNotAvailable = false;

  FileType = FileType;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  player: any;

  src = '';
  checkFor360Video = false;
  videoUploadComplete = true;
  videoCheckSumFail = false;

  showVideoError = false;
  videoLoadErrorMessage = '';
  videoLoadErrorDetail = '';

  constructor(private transloco: TranslocoService) {}

  ngOnInit(): void {
    if (this.videoType === FileType.WastingVideo) {
      this.src = this.workflow?.videoUrls?.videoReadUrl ?? '';
      this.checkFor360Video = true;
      this.videoUploadComplete = this.workflow?.videoUploadComplete ?? false;
      this.videoCheckSumFail = this.workflow?.videoUrls?.videoCheckSumFail ?? true;
    } else if (this.videoType === FileType.WitnessVideo) {
      this.src = this.workflow?.videoUrls?.witnessVideoReadUrl ?? '';
    } else if (this.videoType === FileType.ScreenVideo) {
      this.src = this.workflow?.videoUrls?.screenVideoReadUrl ?? '';
      this.videoUploadComplete = isWasting(this.workflow)
        ? this.workflow.screenRecordingVideoUploadComplete
        : isTransport(this.workflow)
          ? this.workflow.screenVideoUploadComplete
          : false;
    }
  }

  ngAfterViewInit() {
    this.player = videojs(
      this.target.nativeElement,
      {
        fluid: this.fluid,
        aspectRatio: this.aspectRatio,
        autoplay: this.autoplay,
        controls: !this.disabled,
        sources: [{ src: this.src, type: this.type }],
        inactivityTimeout: this.inactivityTimeout,
        disablePictureInPicture: true,
        controlBar: { pictureInPictureToggle: false },
      },
      function onPlayerReady() {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // console.log('onPlayerReady', this);
      },
    );

    this.target.nativeElement.addEventListener('loadedmetadata', (e: Event) => {
      const video = e.target as HTMLVideoElement;
      this.metadataLoaded.emit({ duration: video.duration });

      if (this.checkFor360Video && this.workflow?.is360Video) {
        this.player.vr({ projection: 'Sphere' });
      }
    });
  }

  ngOnDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  }

  @HostListener('window:blur')
  onBlur(): void {
    this.player.pause();
  }

  handleVideoError(e: ErrorEvent): void {
    this.showVideoError = true;
    this.videoFailedEvent.emit(true);
    this.videoLoadErrorMessage = this.transloco.translate('WitnessPlayVideoError');

    if (!e) {
      this.videoLoadErrorDetail =
        this.transloco.translate('ErrorDetail') + ': ' + this.transloco.translate('UnknownError');
      return;
    }

    const video = e.target as HTMLVideoElement;
    this.videoLoadErrorDetail = this.transloco.translate('ErrorDetail') + ': ';

    switch (video.error?.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        this.videoLoadErrorDetail =
          this.videoLoadErrorDetail + this.transloco.translate('FetchAbortedByUser');
        break;
      case MediaError.MEDIA_ERR_NETWORK:
        this.videoLoadErrorDetail = this.videoLoadErrorDetail + this.transloco.translate('MediaNetworkIssue');
        break;
      case MediaError.MEDIA_ERR_DECODE:
        this.videoLoadErrorDetail = this.videoLoadErrorDetail + this.transloco.translate('MediaDecodeError');
        break;
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        this.videoLoadErrorDetail =
          this.videoLoadErrorDetail + this.transloco.translate('MediaSourceUnavailable');
        break;
      default:
        this.videoLoadErrorDetail =
          this.videoLoadErrorDetail +
          this.transloco.translate('UnknownMediaErrorCode') +
          ': ' +
          video.error?.code;
    }
  }
}
